/**
 * Estado de visão do cartão flutuante do painel (§5.1): `aberto`, `recolhido`
 * (botão compacto no canto) ou `sobreposto` (aberto POR CIMA do chat).
 *
 * - Faixa ao lado do trilho cabe o cartão (`cabeCartaoNaFaixa`): aberto, a não
 *   ser que o usuário tenha recolhido (preferência em `localStorage`).
 * - Não cabe: sempre recolhido; o clique abre sobreposto, que fecha com o
 *   chevron, ESC ou clique fora (mesma receita do
 *   `chat-indice-artefatos-maximizado-mixin.js`) — ou sozinho quando a faixa
 *   passa a caber.
 * - Turno com plano ao vivo: abre a seção Progresso e, se couber, abre o
 *   cartão mesmo recolhido (sem gravar preferência). Sem faixa NÃO sobrepõe
 *   sozinho: o cartão nunca cobre o texto sem o usuário pedir; o anel do botão
 *   recolhido já mostra o andamento.
 *
 * - ★ Aside do artefato ABERTO ("fica só o chat", estilo ChatGPT): o cartão
 *   sai da frente — vira o botão recolhido (o clique abre SOBREPOSTO, para
 *   navegar entre entregas) ou, se a faixa não comporta nem o botão
 *   (`cabeBotaoRecolhidoNaFaixa`; com o aside a 50% ela costuma ser 0), fica
 *   `oculto`: o botão cobriria a bolha do usuário. É visão DERIVADA — não
 *   grava a preferência; fechado o aside, volta ao que era.
 *
 * Consome do componente `larguraMaxima`, `turnoEmVoo` e `lerDoPainel`.
 */
import { cabeBotaoRecolhidoNaFaixa, cabeCartaoNaFaixa } from './painel-do-chat.constantes'
import { gravarPrefereRecolhido, lerPrefereRecolhido } from './preferencia-de-recolhido'

const TECLA_ESC = 'Escape'

export const VISOES_DO_CARTAO = Object.freeze({
  aberto: 'aberto',
  recolhido: 'recolhido',
  sobreposto: 'sobreposto',
  oculto: 'oculto',
})

export default {
  data() {
    return {
      prefereRecolhido: lerPrefereRecolhido(),
      abertoPorCima: false,
      abertoPeloTurno: false,
      progressoAberto: false,
    }
  },
  computed: {
    cabeCartao() {
      return cabeCartaoNaFaixa(this.larguraMaxima)
    },
    asideAberto() {
      return Boolean(this.lerDoPainel('artefatoAberto'))
    },
    visaoDoCartao() {
      if (this.asideAberto) return this.visaoComAsideAberto()
      if (!this.cabeCartao) {
        return this.abertoPorCima ? VISOES_DO_CARTAO.sobreposto : VISOES_DO_CARTAO.recolhido
      }
      if (this.prefereRecolhido && !this.abertoPeloTurno) return VISOES_DO_CARTAO.recolhido
      return VISOES_DO_CARTAO.aberto
    },
    recolhido() {
      return this.ehVisao(VISOES_DO_CARTAO.recolhido)
    },
    sobreposto() {
      return this.ehVisao(VISOES_DO_CARTAO.sobreposto)
    },
    oculto() {
      return this.ehVisao(VISOES_DO_CARTAO.oculto)
    },
    planoAoVivoComItens() {
      if (!this.turnoEmVoo) return false
      const plano = this.lerDoPainel('plano')
      return Boolean(plano && plano.itens && plano.itens.length)
    },
    // Há etapa a mostrar? (plano vivo OU `message.plano` do histórico — quem
    // resolve os dois é `progressoDoPainel`, em `dados-do-painel-mixin.js`.)
    temEtapasNoProgresso() {
      const passos = this.progressoDoPainel
      return Boolean(passos && passos.length)
    },
  },
  watch: {
    sobreposto(ativo) {
      if (ativo) {
        document.addEventListener('mousedown', this.fecharSobrepostoSeFora)
        document.addEventListener('keydown', this.fecharSobrepostoComEsc)
        return
      }
      this.desligarFechamentoDoSobreposto()
    },
    cabeCartao(cabe) {
      if (cabe && !this.asideAberto) this.abertoPorCima = false
    },
    asideAberto() {
      this.abertoPorCima = false
    },
    planoAoVivoComItens: {
      immediate: true,
      handler(tem) {
        if (!tem) return
        this.progressoAberto = true
        this.abertoPeloTurno = true
      },
    },
    // ★ **[18/09/2026] Ter etapas já basta para a seção nascer ABERTA.**
    //
    // O watcher acima só dispara com plano AO VIVO (`turnoEmVoo`), então entre
    // turnos e em toda reabertura a seção nascia recolhida — e o onboarding é
    // justamente onde as etapas importam: elas SÃO o que a pessoa está fazendo
    // (owner, 18/09/2026: *"essa parte de progresso, no onboarding poderia
    // criar expandida né?"*). Aqui a pergunta é sobre o progresso RENDERIZADO
    // (`progressoDoPainel`, que lê o plano vivo E o `message.plano` do
    // histórico), não sobre o turno.
    //
    // ⚠️ `progressoAberto` continua sendo o estado do USUÁRIO: isto só abre, e
    // só na BORDA de "não tinha etapa" → "tem etapa". Recolher continua
    // valendo — quem fechou a seção não a vê reabrir sozinha a cada render.
    temEtapasNoProgresso: {
      immediate: true,
      handler(tem, tinha) {
        if (!tem || tinha) return
        this.progressoAberto = true
      },
    },
  },
  beforeDestroy() {
    this.desligarFechamentoDoSobreposto()
  },
  methods: {
    // Comparação única das três visões derivadas (`recolhido`/`sobreposto`/
    // `oculto`) — o `visaoDoCartao` é a fonte, aqui só se pergunta qual é.
    ehVisao(alvo) {
      return this.visaoDoCartao === alvo
    },
    visaoComAsideAberto() {
      if (!cabeBotaoRecolhidoNaFaixa(this.larguraMaxima)) return VISOES_DO_CARTAO.oculto
      return this.abertoPorCima ? VISOES_DO_CARTAO.sobreposto : VISOES_DO_CARTAO.recolhido
    },
    expandir() {
      if (!this.cabeCartao || this.asideAberto) {
        this.abertoPorCima = true
        return
      }
      this.prefereRecolhido = false
      gravarPrefereRecolhido(false)
    },
    recolher() {
      if (this.sobreposto) {
        this.abertoPorCima = false
        return
      }
      this.abertoPeloTurno = false
      this.prefereRecolhido = true
      gravarPrefereRecolhido(true)
    },
    alternarProgresso() {
      this.progressoAberto = !this.progressoAberto
    },
    desligarFechamentoDoSobreposto() {
      document.removeEventListener('mousedown', this.fecharSobrepostoSeFora)
      document.removeEventListener('keydown', this.fecharSobrepostoComEsc)
    },
    fecharSobrepostoSeFora(evento) {
      if (this.$el && this.$el.contains && this.$el.contains(evento.target)) return
      this.abertoPorCima = false
    },
    fecharSobrepostoComEsc(evento) {
      if (evento.key !== TECLA_ESC) return
      this.abertoPorCima = false
    },
  },
}
